
import * as React from "react";
import Popover from "@mui/material/Popover";
import toast from "react-hot-toast";
import { User, Users, Milestone, Plus, Pencil, Trash2 } from "lucide-react";
import SubscriptionFormDrawer from "./SubscriptionFormDrawer";
import {
  createTemplate,
  deleteTemplate,
  fetchContacts,
  fetchMilestones,
  fetchTemplates,
  fetchWhatsappGroups,
  updateTemplate,
} from "./subscriptionTemplate.service";
import {
  AirSubscriptionTemplateProps,
  Contact,
  DrawerState,
  Milestone as MilestoneType,
  StatusFilter,
  TemplateRow,
  WhatsappGroup,
} from "./subscriptionTemplate.types";
import SearchInput from "../../common/SearchInput";

const PAGE_SIZE = 10;

const emptyDrawerState: DrawerState = {
  id: null,
  templateName: "",
  waContacts: [],
  waMilestones: [],
  emailContacts: [],
  emailMilestones: [],
  whatsappGroups: [],
  whatsappGroupMilestones: [],
  isActive: true,
};

/* ── Elegant, theme-matched count badge ──────────────────────────────────
   icon + bold count + muted label, coloured by channel (wa=green, email=blue),
   greyed when empty. Clicking opens the existing list popover. */
function ChannelBadge({
  variant,
  icon,
  count,
  label,
  onClick,
}: {
  variant: "wa" | "email";
  icon: React.ReactNode;
  count: number;
  label: string;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
}) {
  const empty = count === 0;
  const plural = count === 1 ? label : `${label}s`;
  return (
    <button
      type="button"
      className={`ctpl-channel-badge ${variant}`}
      onClick={onClick}
      title={`${count} ${plural}`}
      style={
        empty
          ? { background: "#f2f4f7", color: "var(--ctpl-ink-soft)" }
          : undefined
      }
    >
      {icon}
      <span style={{ fontWeight: 700 }}>{count}</span>
      <span style={{ opacity: 0.7, fontWeight: 500 }}>{plural}</span>
    </button>
  );
}

export default function SubscriptionTemplate({ mode }: AirSubscriptionTemplateProps) {
  const [rows, setRows] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [milestonedata, setMilestonedata] = React.useState<MilestoneType[]>([]);
  const [waContacts, setWaContacts] = React.useState<Contact[]>([]);
  const [emailContacts, setEmailContacts] = React.useState<Contact[]>([]);
  const [whatsappGroup, setWhatsappGroup] = React.useState<WhatsappGroup[]>([]);

  const [searchQuery, setSearchQuery] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>("all");
  const [currentPage, setCurrentPage] = React.useState(0);

  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [drawerState, setDrawerState] = React.useState<DrawerState>(emptyDrawerState);

  const [popover, setPopover] = React.useState<{ anchor: HTMLElement; content: React.ReactNode } | null>(null);

  const getTemplatedata = React.useCallback(
    async (search: string, status: StatusFilter, currentMode: string) => {
      setLoading(true);
      try {
        setRows(await fetchTemplates(search, status, currentMode));
      } catch {
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 500);
    return () => clearTimeout(t);
  }, [searchQuery]);

  React.useEffect(() => {
    getTemplatedata(debouncedSearch, statusFilter, mode);
  }, [debouncedSearch, statusFilter, mode, getTemplatedata]);

  React.useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearch, statusFilter, mode]);

  React.useEffect(() => {
    (async () => setMilestonedata(await fetchMilestones(mode)))();
  }, [mode]);

  React.useEffect(() => {
    (async () => {
      const c = await fetchContacts();
      setWaContacts(c.whatsApp);
      setEmailContacts(c.email);
      setWhatsappGroup(await fetchWhatsappGroups());
    })();
  }, []);

  const refetch = () => getTemplatedata(debouncedSearch, statusFilter, mode);

  const mappedRows: TemplateRow[] = React.useMemo(
    () =>
      rows.map((row: any) => ({
        ...row,
        id: row?._id || row.id,
        templatename: row?.templateName,
        waContacts: row?.waContacts || [],
        waMilestones: row?.waMilestones || [],
        emailContacts: row?.emailContacts || [],
        emailMilestones: row?.emailMilestones || [],
        whatsappGroups: row?.whatsappGroups || [],
        whatsappGroupMilestones: row?.whatsappGroupMilestones || [],
        isActive: row?.isActive !== false,
      })),
    [rows]
  );

  const pageCount = Math.max(1, Math.ceil(mappedRows.length / PAGE_SIZE));
  const pagedRows = mappedRows.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE);

  const openAddDrawer = () => {
    setDrawerState(emptyDrawerState);
    setDrawerOpen(true);
  };

  const openEditDrawer = (row: TemplateRow) => {
    setDrawerState({
      id: row.id,
      templateName: row.templatename || "",
      waContacts: row.waContacts || [],
      waMilestones: row.waMilestones || [],
      emailContacts: row.emailContacts || [],
      emailMilestones: row.emailMilestones || [],
      whatsappGroups: row.whatsappGroups || [],
      whatsappGroupMilestones: row.whatsappGroupMilestones || [],
      isActive: row.isActive !== false,
    });
    setDrawerOpen(true);
  };

  const closeDrawer = () => setDrawerOpen(false);

  const handleSaveTemplate = async () => {
    const payload = {
      templateName: drawerState.templateName,
      waContacts: drawerState.waContacts.map((c) => c?._id),
      waMilestones: drawerState.waMilestones.map((m) => m?._id),
      emailContacts: drawerState.emailContacts.map((c) => c?._id),
      emailMilestones: drawerState.emailMilestones.map((m) => m?._id),
      whatsappGroups: drawerState.whatsappGroups.map((g) => g?._id),
      whatsappGroupMilestones: drawerState.whatsappGroupMilestones.map((m) => m?._id),
      isActive: drawerState.isActive,
    };

    try {
      if (!drawerState.id) {
        const response = await createTemplate(payload, mode);
        if (response.data.statusCode === 201) {
          toast.success(response.data.message);
          setCurrentPage(0);
          refetch();
          closeDrawer();
        }
      } else {
        const response = await updateTemplate(drawerState.id, payload, mode);
        const errorData = response.data?.error;
        if (typeof errorData === "object" && !Array.isArray(errorData) && errorData) {
          Object.keys(errorData).forEach((f) => toast.error(`${f}: ${errorData[f]}`));
        } else {
          toast.success(response.data.message || "Template updated successfully");
          refetch();
          closeDrawer();
        }
      }
    } catch (err: any) {
      const errors = err?.response?.data?.error;
      if (Array.isArray(errors)) errors.forEach((e: any) => toast.error(`${e.field}: ${e.message}`));
      else if (typeof errors === "object" && errors) Object.keys(errors).forEach((f) => toast.error(`${f}: ${errors[f]}`));
      else if (typeof errors === "string") toast.error(errors);
      else toast.error("An error occurred while saving the template.");
    }
  };

  const handleDeleteClick = async (row: TemplateRow) => {
    try {
      const response = await deleteTemplate(row.id, mode);
      refetch();
      if (response.status === 200) toast.success(response.data.message || "Template deleted successfully.");
    } catch {
      toast.error("An error occurred while deleting the template.");
    }
  };

  const showListPopover = (e: React.MouseEvent<HTMLElement>, title: string, items: string[]) => {
    setPopover({
      anchor: e.currentTarget,
      content: (
        <>
          <div className="ctpl-popover-title">{title} ({items.length})</div>
          <ul className="ctpl-popover-list">
            {items.length ? items.map((it, i) => <li key={i}>{it}</li>) : <li>None</li>}
          </ul>
        </>
      ),
    });
  };

  const modeLabel = mode === "air" ? "Air" : "Ocean";
  const contactName = (c: Contact) => `${c.salutation || ""}. ${c.name || ""}`.trim();
  const msLabel = (m: MilestoneType) => m.label || m.milestone || "";

  return (
    <>
      <div className="ctpl-card">
        <div className="ctpl-toolbar">
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search templates…"
          />

          <div className="ctpl-spacer" />

          <button className="ctpl-btn ctpl-btn-primary" onClick={openAddDrawer}>
            <Plus size={16} />
            Add Template
          </button>
        </div>

        <div className="ctpl-table-scroll">
          <table className="ctpl-table">
            <thead>
              <tr>
                <th style={{ width: "16%" }}>Template</th>
                <th style={{ width: "23%" }}>WhatsApp</th>
                <th style={{ width: "23%" }}>Email</th>
                <th style={{ width: "23%" }}>WhatsApp Groups</th>
                <th style={{ width: "10%", textAlign: "right", paddingRight: 22 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="ctpl-empty-cell" colSpan={5}>Loading templates…</td></tr>
              ) : pagedRows.length === 0 ? (
                <tr><td className="ctpl-empty-cell" colSpan={5}>No templates found</td></tr>
              ) : (
                pagedRows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <div className="ctpl-tmpl-name">{row.templatename}</div>
                      <div className="ctpl-tmpl-sub">{modeLabel}</div>
                    </td>
                    <td>
                      <div className="ctpl-channel-badges">
                        <ChannelBadge
                          variant="wa"
                          icon={<User size={14} />}
                          count={row.waContacts.length}
                          label="recipient"
                          onClick={(e) => showListPopover(e, "WhatsApp recipients", row.waContacts.map(contactName))}
                        />
                        <ChannelBadge
                          variant="wa"
                          icon={<Milestone size={14} />}
                          count={row.waMilestones.length}
                          label="milestone"
                          onClick={(e) => showListPopover(e, "WhatsApp milestones", row.waMilestones.map(msLabel))}
                        />
                      </div>
                    </td>
                    <td>
                      <div className="ctpl-channel-badges">
                        <ChannelBadge
                          variant="email"
                          icon={<User size={14} />}
                          count={row.emailContacts.length}
                          label="recipient"
                          onClick={(e) => showListPopover(e, "Email recipients", row.emailContacts.map(contactName))}
                        />
                        <ChannelBadge
                          variant="email"
                          icon={<Milestone size={14} />}
                          count={row.emailMilestones.length}
                          label="milestone"
                          onClick={(e) => showListPopover(e, "Email milestones", row.emailMilestones.map(msLabel))}
                        />
                      </div>
                    </td>
                    <td>
                      <div className="ctpl-channel-badges">
                        <ChannelBadge
                          variant="wa"
                          icon={<Users size={14} />}
                          count={row.whatsappGroups.length}
                          label="group"
                          onClick={(e) => showListPopover(e, "Groups", row.whatsappGroups.map((g) => g.name || ""))}
                        />
                        <ChannelBadge
                          variant="wa"
                          icon={<Milestone size={14} />}
                          count={row.whatsappGroupMilestones.length}
                          label="milestone"
                          onClick={(e) => showListPopover(e, "Group milestones", row.whatsappGroupMilestones.map(msLabel))}
                        />
                      </div>
                    </td>
                    <td>
                      <div className="ctpl-row-actions">
                        <button className="ctpl-action-circle edit" title={row.isActive ? "Edit" : "Activate to edit"} onClick={() => openEditDrawer(row)} disabled={!row.isActive}>
                          <Pencil size={15} />
                        </button>
                        <button className="ctpl-action-circle delete" title={row.isActive ? "Delete" : "Activate to delete"} onClick={() => handleDeleteClick(row)} disabled={!row.isActive}>
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="ctpl-table-footer">
          <span>{mappedRows.length} template{mappedRows.length === 1 ? "" : "s"}</span>
          <div className="ctpl-pagination">
            <button className="ctpl-page-btn" disabled={currentPage === 0} onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}>‹</button>
            <span>Page {currentPage + 1} of {pageCount}</span>
            <button className="ctpl-page-btn" disabled={currentPage >= pageCount - 1} onClick={() => setCurrentPage((p) => Math.min(pageCount - 1, p + 1))}>›</button>
          </div>
        </div>
      </div>

      <SubscriptionFormDrawer
        open={drawerOpen}
        state={drawerState}
        milestoneOptions={milestonedata}
        waContactOptions={waContacts}
        emailContactOptions={emailContacts}
        groupOptions={whatsappGroup}
        onChange={setDrawerState}
        onClose={closeDrawer}
        onSave={handleSaveTemplate}
      />

      <Popover
        open={!!popover}
        anchorEl={popover?.anchor}
        onClose={() => setPopover(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{ className: "ctpl-popover-paper" }}
      >
        {popover?.content}
      </Popover>
    </>
  );
}